/**
 * Admin · Products System (P4) · Catalogs — list + create.
 *
 * A Catalog is a named collection of products (imported from any URL or added
 * manually), branded by an issuing company, from which the operator can export
 * a branded catalogue PDF and raise a Proforma / Invoice in Finance.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Plus, FolderOpen, Trash2, Loader2, Package, ArrowRight, X,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CURRENCIES = ['AED', 'USD', 'EUR', 'GBP', 'AOA'];

const CatalogsList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [catalogs, setCatalogs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', currency: 'AED', company_id: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [c, co] = await Promise.all([
        axios.get(`${API_URL}/api/admin/catalogs`, auth),
        axios.get(`${API_URL}/api/procurement/companies`, auth),
      ]);
      setCatalogs(c.data || []);
      setCompanies(co.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load catalogs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const companyName = (id) => {
    const c = companies.find((x) => x.id === id);
    return c ? (c.trading_name || c.legal_name) : '—';
  };

  const create = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/catalogs`, form, auth);
      setShowCreate(false);
      setForm({ name: '', description: '', currency: 'AED', company_id: '' });
      toast({ title: 'Catalog created' });
      navigate(`/admin/catalogs/${r.data.id}`);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not create catalog', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this catalog?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/catalogs/${id}`, auth);
      setCatalogs((prev) => prev.filter((c) => c.id !== id));
      toast({ title: 'Catalog deleted' });
    } catch (e2) {
      toast({ title: 'Error', description: 'Could not delete', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="catalogs-page">
      <BackButton />
      <div className="flex items-center justify-between mt-4 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0F172A] flex items-center gap-2">
            <FolderOpen className="w-6 h-6 text-[#FF6B35]" /> Catalogs
          </h1>
          <p className="text-sm text-[#6C757D] mt-1">
            Import products from any URL, build branded catalogues and raise proformas &amp; invoices.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="new-catalog-btn"
                className="bg-[#0F172A] hover:bg-[#1E293B]">
          <Plus className="w-4 h-4 mr-2" /> New Catalog
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#6C757D]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      ) : catalogs.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-2xl p-16 text-center" data-testid="catalogs-empty">
          <Package className="w-10 h-10 text-slate-300 mx-auto mb-4" />
          <p className="text-[#6C757D] mb-4">No catalogs yet. Create your first one.</p>
          <Button onClick={() => setShowCreate(true)} className="bg-[#FF6B35] hover:bg-[#F97316]">
            <Plus className="w-4 h-4 mr-2" /> New Catalog
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {catalogs.map((c) => (
            <div key={c.id}
                 onClick={() => navigate(`/admin/catalogs/${c.id}`)}
                 data-testid={`catalog-card-${c.id}`}
                 className="group bg-white border border-slate-200 rounded-2xl p-5 cursor-pointer hover:shadow-lg hover:border-[#00B4D8] transition-all">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <h3 className="font-extrabold text-[#0F172A] truncate">{c.name}</h3>
                  <p className="text-xs text-[#6C757D] mt-1 line-clamp-2">{c.description || '—'}</p>
                </div>
                <button onClick={(e) => remove(c.id, e)} data-testid={`delete-catalog-${c.id}`}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                  <Package className="w-3 h-3 mr-1" /> {c.item_count} items
                </Badge>
                <Badge variant="secondary" className="bg-cyan-50 text-cyan-700">{c.currency}</Badge>
                <Badge variant="secondary" className="bg-orange-50 text-[#FF6B35] truncate max-w-[140px]">
                  {companyName(c.company_id)}
                </Badge>
              </div>
              <div className="flex items-center justify-end mt-4 text-[#00B4D8] text-sm font-semibold">
                Open <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition" />
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-catalog-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-extrabold text-[#0F172A]">New Catalog</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 rounded hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#6C757D]">Name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="catalog-name-input"
                       className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#00B4D8]"
                       placeholder="e.g. Corporate IT — Q3 2026" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#6C757D]">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                          rows={2}
                          className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#00B4D8]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#6C757D]">Currency</label>
                  <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                          data-testid="catalog-currency-select"
                          className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-[#6C757D]">Brand (company)</label>
                  <select value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                          data-testid="catalog-company-select"
                          className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                    <option value="">Default company</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.trading_name || c.legal_name}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={create} disabled={saving} data-testid="create-catalog-submit"
                      className="bg-[#0F172A] hover:bg-[#1E293B]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogsList;
